/**
 * GlobalSearch — app-wide quick jump (command palette).
 *
 * Opens with Ctrl/Cmd-K or the floating button. Searches pages, people
 * (clients + uplines), and accounts; Enter / click navigates and closes.
 *
 * Uses its own portal overlay (not the shared Modal) on purpose: Modal pushes
 * a history entry and pops it on close, which would fight with the navigation
 * we trigger on select. Here we navigate first, then close — no history games.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Search, X } from 'lucide-react';
import { api, type Account, type Party } from '../api';
import { SECTION_LIST } from './sections';

type Hit = {
  key: string;
  label: string;
  sub: string;
  to: string;
  kind: 'page' | 'person' | 'account';
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Global open shortcut: Ctrl/Cmd-K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      // Focus after the portal paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
    enabled: open,
    staleTime: 30_000,
  });
  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts', 'search-all'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
    enabled: open,
    staleTime: 30_000,
  });

  const hits = useMemo<Hit[]>(() => {
    const pages: Hit[] = SECTION_LIST.map((s) => ({
      key: `page:${s.to}`,
      label: s.label,
      sub: s.mobileHint,
      to: s.to,
      kind: 'page',
    }));
    const people: Hit[] = (partiesQ.data ?? [])
      .filter((p) => p.role !== 'self')
      .map((p) => ({
        key: `party:${p.id}`,
        label: p.displayName,
        sub: p.status === 'archived' ? `${p.role} · archived` : p.role,
        to: `/parties/${p.id}/ledger`,
        kind: 'person',
      }));
    const accounts: Hit[] = (accountsQ.data ?? []).map((a) => ({
      key: `account:${a.id}`,
      label: a.canonicalName,
      sub: [a.panel, a.status].filter(Boolean).join(' · ') || 'account',
      to: '/accounts',
      kind: 'account',
    }));

    const all = [...pages, ...people, ...accounts];
    const needle = q.trim().toLowerCase();
    if (!needle) return pages; // empty query → just the nav pages
    return all
      .filter((h) => h.label.toLowerCase().includes(needle) || h.sub.toLowerCase().includes(needle))
      .slice(0, 40);
  }, [partiesQ.data, accountsQ.data, q]);

  function go(h: Hit | undefined) {
    if (!h) return;
    nav(h.to);
    setOpen(false);
  }

  return (
    <>
      {/* Floating trigger — sits above the mobile dock. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search (Ctrl+K)"
        title="Search  ·  Ctrl/⌘ + K"
        className="fixed right-4 z-[90] flex h-12 w-12 items-center justify-center rounded-full border border-border bg-surface text-ink shadow-card transition-transform hover:scale-105 active:scale-95"
        style={{
          bottom: 'calc(var(--mobile-dock-height, 6.2rem) + env(safe-area-inset-bottom) + 0.75rem)',
        }}
      >
        <Search className="h-5 w-5" strokeWidth={2.2} />
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[110] flex items-start justify-center bg-black/60 p-4 pt-[12vh]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <div
              className="flex max-h-[72vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-card"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
                <Search className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActive((i) => Math.min(i + 1, hits.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActive((i) => Math.max(i - 1, 0));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      go(hits[active]);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setOpen(false);
                    }
                  }}
                  placeholder="Search people, accounts, pages…"
                  className="min-w-0 flex-1 bg-transparent text-body text-ink placeholder:text-ink-muted focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded-md p-1 text-ink-muted hover:bg-surface-raised"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {hits.length === 0 ? (
                  <div className="px-3 py-8 text-center text-small text-ink-muted">
                    No matches{q.trim() ? ` for “${q.trim()}”` : ''}.
                  </div>
                ) : (
                  hits.map((h, i) => (
                    <button
                      key={h.key}
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                        i === active ? 'bg-surface-raised' : 'hover:bg-surface-raised'
                      }`}
                    >
                      <span
                        className={`shrink-0 rounded-md px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide ${kindChip(h.kind)}`}
                      >
                        {h.kind === 'page' ? 'Page' : h.kind === 'person' ? 'Person' : 'Acct'}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-body font-medium text-ink">{h.label}</span>
                        <span className="block truncate text-caption text-ink-muted">{h.sub}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
                    </button>
                  ))
                )}
              </div>

              <div className="border-t border-border px-3 py-1.5 text-caption text-ink-muted">
                ↑↓ to move · Enter to open · Esc to close
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function kindChip(kind: Hit['kind']): string {
  if (kind === 'page') return 'bg-sec-dashboard-tint text-sec-dashboard';
  if (kind === 'person') return 'bg-sec-people-tint text-sec-people';
  return 'bg-sec-accounts-tint text-sec-accounts';
}
